import classNames from 'classnames';

import { ALL_PLATFORMS, PLATFORM_LABEL, type Platform } from '../types';
import { PlatformIcon } from './PlatformIcon';

interface PlatformFilterProps {
  selected: ReadonlySet<Platform>;
  counts: Record<Platform, number>;
  onChange: (next: Set<Platform>) => void;
}

// Segmented control: "All" + one segment per platform. Clicking a single platform narrows to
// just it; clicking "All" restores all platforms. Counts reflect the unfiltered findings for
// the active template.
export const PlatformFilter = ({ selected, counts, onChange }: PlatformFilterProps) => {
  const isAll = selected.size === ALL_PLATFORMS.length;
  const totalCount = ALL_PLATFORMS.reduce((sum, p) => sum + (counts[p] ?? 0), 0);

  const selectAll = () => onChange(new Set(ALL_PLATFORMS));
  const selectOne = (p: Platform) => onChange(new Set([p]));

  return (
    <div className='platform-filter'>
      <span className='platform-filter__label'>Platform</span>
      <div className='platform-filter__group' role='radiogroup' aria-label='Platform filter'>
        <PlatformFilterSegment label='All' count={totalCount} active={isAll} onClick={selectAll} />
        {ALL_PLATFORMS.map(p => (
          <PlatformFilterSegment
            key={p}
            label={PLATFORM_LABEL[p]}
            count={counts[p] ?? 0}
            active={!isAll && selected.has(p)}
            platform={p}
            onClick={() => selectOne(p)}
          />
        ))}
      </div>
    </div>
  );
};

interface SegmentProps {
  label: string;
  count: number;
  active: boolean;
  platform?: Platform;
  onClick: () => void;
}

const PlatformFilterSegment = ({ label, count, active, platform, onClick }: SegmentProps) => (
  <button
    type='button'
    role='radio'
    aria-checked={active}
    className={classNames(
      'platform-filter__segment',
      active && 'platform-filter__segment--active',
      platform && `platform-filter__segment--${platform}`,
      count === 0 && 'platform-filter__segment--empty'
    )}
    onClick={onClick}
  >
    {platform && <PlatformIcon platform={platform} size={16} />}
    {label} <span className='platform-filter__count'>{count}</span>
  </button>
);
